import { useState } from 'react';
import clsx from 'clsx';

import { Collapse, Icon, IconButton, Switch } from '@atom/mui';
import {
  AddIcon,
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
  LayersIcon,
} from '@atom/mui/icons';
import type { ArcgisLayer } from '@atom/types/arcgis';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import { useArcgisLayers } from '../arcgis/ArcgisLayersProvider';
import { useKmlLayers } from '../kml/KmlLayersProvider';
import { useMapLayers } from '../MapPortalDataProvider';

import ArcgisLayerRow from './ArcgisLayerRow';
import GeospatialLayerModal from './GeospatialLayerModal';
import KmlLayerRow from './KmlLayerRow';

import classes from './filterPanel.module.css';

const GeospatialLayersSection = () => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { layers, setLayer, setFilter } = useMapLayers();
  const { geospatialLayers: toggled, kmlLayerIds, arcgisLayerIds } = layers;
  const { kmlDictionary } = useKmlLayers();
  const { arcgisDictionary } = useArcgisLayers();

  const canManage = hasRolePermissions(ROLE_SETS.ORG_ADMIN);

  const allIds = [
    ...kmlDictionary.map(layer => layer.id),
    ...arcgisDictionary.map(layer => layer.id),
  ];

  const selectAll = () => {
    setFilter('kmlLayerIds', new Set(kmlDictionary.map(layer => layer.id)));
    setFilter(
      'arcgisLayerIds',
      new Set(arcgisDictionary.map(layer => layer.id)),
    );
  };

  const clearAll = () => {
    setFilter('kmlLayerIds', new Set());
    setFilter('arcgisLayerIds', new Set());
  };

  const allSelected =
    allIds.length > 0 &&
    kmlDictionary.every(layer => kmlLayerIds.has(layer.id)) &&
    arcgisDictionary.every(layer => arcgisLayerIds.has(layer.id));
  const noneSelected = kmlLayerIds.size === 0 && arcgisLayerIds.size === 0;

  const handleCreated = (created: ArcgisLayer) => {
    setFilter('arcgisLayerIds', new Set(arcgisLayerIds).add(created.id));

    if (!toggled) {
      setLayer('geospatialLayers', true);
    }
  };

  return (
    <div className={classes.sectionContainer}>
      <div className={classes.sectionHeaderContainer}>
        <Switch
          label={
            <div className={classes.toggleLabel}>
              <div className={classes.sectionHeaderIcon}>
                <Icon icon={LayersIcon} />
              </div>
              <div>Geospatial Layers</div>
            </div>
          }
          checked={toggled}
          onChange={() => setLayer('geospatialLayers', !toggled)}
          data-cy="dashboardGeospatialToggle"
        />
        <div className={classes.headerActions}>
          {canManage && (
            <IconButton
              tooltip="Add geospatial layer"
              onClick={() => setModalOpen(true)}
              data-cy="dashboardGeospatialAdd"
            >
              <Icon icon={AddIcon} />
            </IconButton>
          )}
          <IconButton
            edge="end"
            onClick={() => setExpanded(!expanded)}
            data-cy="dashboardGeospatialExpand"
          >
            <Icon
              icon={expanded ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
            />
          </IconButton>
        </div>
      </div>
      <div className={classes.sectionFilters}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <div className={classes.selectAllContainer}>
            <div
              onClick={selectAll}
              className={clsx(
                classes.selectAllLabel,
                (!toggled || allSelected) && classes.disabled,
              )}
              data-cy="dashboardGeospatialSelectAll"
            >
              Select All
            </div>
            <div className={classes.selectAllDivider} />
            <div
              onClick={clearAll}
              className={clsx(
                classes.selectAllLabel,
                (!toggled || noneSelected) && classes.disabled,
              )}
              data-cy="dashboardGeospatialClearAll"
            >
              Clear All
            </div>
          </div>
          {kmlDictionary.map(layer => (
            <KmlLayerRow key={layer.id} layer={layer} />
          ))}
          {arcgisDictionary.map(layer => (
            <ArcgisLayerRow key={layer.id} layer={layer} />
          ))}
        </Collapse>
      </div>
      <GeospatialLayerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleCreated}
      />
    </div>
  );
};

export default GeospatialLayersSection;
